import React, { useState, useEffect } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  quotePlugin,
  headingsPlugin,
  tablePlugin,
  thematicBreakPlugin,
  frontmatterPlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  markdownShortcutPlugin,
  BlockTypeSelect,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  Separator
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import '../agent-training.css';
import { CancelButton, PrimaryButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';

// Compact MDXEditor wrapper with smaller buttons
const CompactMDXEditor = ({ value, onChange, placeholder, minHeight = '150px' }) => {
  return (
    <div style={{
      border: '2px solid #f0f0f0',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#fff',
      transition: 'all 0.3s ease',
      minWidth: 0,
      flexShrink: 1,
    }}>
      <style>{`
        .mdxeditor {
          font-size: 14px;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          --mdxeditor-color: #2c1810;
          --mdxeditor-border: #f0f0f0;
          min-width: 0;
          max-width: 100%;
        }
        .mdxeditor-toolbar {
          border-bottom: 1px solid #f0f0f0;
          background-color: #fafbfc;
          padding: 8px 12px;
          min-height: 40px;
          display: flex;
          align-items: center;
          gap: 4px;
          min-width: 0;
          flex-wrap: wrap;
        }
        .mdxeditor-root-contenteditable {
          min-height: ${minHeight};
          font-size: 14px;
          padding: 0;
          line-height: 1.6;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }
        .mdxeditor-toolbar button {
          border-radius: 6px;
          padding: 6px 10px;
          transition: all 0.2s ease;
          font-size: 13px;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .mdxeditor-toolbar button:hover {
          background-color: rgba(85, 41, 127, 0.1);
          transform: translateY(-1px);
        }
        .mdxeditor-toolbar select {
          font-size: 13px;
          padding: 4px 8px;
          height: 32px;
          border: 1px solid #ddd;
          border-radius: 6px;
          background: white;
        }
        .mdxeditor-toolbar .mdxeditor-separator {
          height: 16px;
          margin: 0 2px;
        }
        .mdxeditor-toolbar label {
          position: absolute;
          left: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }
        .mdxeditor-toolbar button svg {
          width: 16px;
          height: 16px;
        }
        .mdxeditor-dialog {
          z-index: 9999;
        }
        .mdxeditor-popup-container {
          z-index: 9999;
        }
        @media (max-width: 768px) {
          .mdxeditor-toolbar {
            padding: 6px 8px;
          }
        }
      `}</style>
      <MDXEditor
        markdown={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        plugins={[
          headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
          listsPlugin(),
          quotePlugin(),
          tablePlugin(),
          thematicBreakPlugin(),
          codeBlockPlugin({
            defaultCodeBlockLanguage: 'text',
            codeBlockEditorDescriptors: []
          }),
          codeMirrorPlugin({
            codeBlockLanguages: {
              text: 'Plain text',
              javascript: 'JavaScript',
              typescript: 'TypeScript',
              css: 'CSS',
              html: 'HTML',
              python: 'Python',
              json: 'JSON',
              bash: 'Bash',
              sql: 'SQL'
            }
          }),
          frontmatterPlugin(),
          markdownShortcutPlugin(),
          toolbarPlugin({
            toolbarContents: () => (
              <>
                <UndoRedo />
                <Separator />
                <BoldItalicUnderlineToggles />
                <Separator />
                <BlockTypeSelect />
                <Separator />
                <ListsToggle />
                <Separator />
                <InsertTable />
                <InsertThematicBreak />
              </>
            )
          })
        ]}
      />
    </div>
  );
};

const AddQAModal = ({ show, onHide, workspaceId, apiUrl, onPollState, initialData }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  // Handle modal state changes
  useEffect(() => {
    if (show && initialData) {
      // Modal is opening with initial data - populate fields
      setTitle(initialData.title || '');
      setQuestion(initialData.question || '');
      setAnswer(initialData.answer || '');
      setEditorKey(prev => prev + 1); // Force editor remount
    } else if (!show) {
      // Modal is closing - reset fields
      setTitle('');
      setQuestion('');
      setAnswer('');
      setError(null);
    }
  }, [show, initialData]);

  const handleClose = () => {
    if (!loading) {
      onHide();
    }
  };

  const handleAddQA = async () => {
    try {
      setLoading(true);
      setError(null);
      const brandvoxToken = localStorage.getItem('brandvox_token');

      // Sanitize text content to fix any malformed emoji/surrogate pairs
      const sanitizeText = (text) => {
        try {
          // Fix unpaired surrogates by encoding and decoding properly
          // This will replace invalid surrogates with the replacement character
          const encoder = new TextEncoder();
          const decoder = new TextDecoder('utf-8', { fatal: false });
          const bytes = encoder.encode(text);
          return decoder.decode(bytes);
        } catch (e) {
          // Fallback: remove all unpaired surrogates
          return text.replace(/[\uD800-\uDFFF]/g, '');
        }
      };

      const body = JSON.stringify({
        workspace_id: workspaceId,
        title: sanitizeText(title),
        question: sanitizeText(question),
        answer: sanitizeText(answer),
      });

      const response = await fetch(`${apiUrl}/api/v1/train/add-qa`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok)
        throw new Error(t('dashboard.training.modals.addqamodal.errorFailed'));

      handleClose();
      await onPollState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isContentTooLong =
    question.length > 10000 ||
    answer.length > 10000;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      centered
      size="lg"
      className="training-display-container"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{initialData ? t('dashboard.training.modals.addqamodal.titleEdit') : t('dashboard.training.modals.addqamodal.titleAdd')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(error || isContentTooLong) && (
          <Alert
            variant='danger'
            onClose={() => setError(null)}
            dismissible={!isContentTooLong}
          >
            {error || t('dashboard.training.modals.addqamodal.errorTooLong')}
          </Alert>
        )}
        <Form>
          <Form.Group className='mb-4'>
            <Form.Label>
              {t('dashboard.training.modals.addqamodal.titleLabel')} <span className="text-danger">{t('dashboard.training.modals.addqamodal.titleRequired')}</span>
            </Form.Label>
            <Form.Control
              type='text'
              value={title}
              placeholder={t('dashboard.training.modals.addqamodal.titlePlaceholder')}
              onChange={(e) => setTitle(e.target.value)}
              disabled={loading}
              maxLength={30}
            />
            <Form.Text>
              {t('dashboard.training.modals.addqamodal.titleCharCount', { count: title.length })}
            </Form.Text>
          </Form.Group>
          <Form.Group className='mb-4'>
            <Form.Label>
              {t('dashboard.training.modals.addqamodal.questionLabel')}
            </Form.Label>
            <CompactMDXEditor
              key={`question-${editorKey}`}
              value={question}
              onChange={setQuestion}
              placeholder={t('dashboard.training.modals.addqamodal.questionPlaceholder')}
              minHeight="120px"
            />
            <Form.Text className={question.length > 10000 ? 'text-danger' : ''}>
              {t('dashboard.training.modals.addqamodal.questionCharCount', { count: question.length })}
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {t('dashboard.training.modals.addqamodal.answerLabel')}
            </Form.Label>
            <CompactMDXEditor
              key={`answer-${editorKey}`}
              value={answer}
              onChange={setAnswer}
              placeholder={t('dashboard.training.modals.addqamodal.answerPlaceholder')}
              minHeight="180px"
            />
            <Form.Text className={answer.length > 10000 ? 'text-danger' : ''}>
              {t('dashboard.training.modals.addqamodal.answerCharCount', { count: answer.length })}
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton className='cancel-btn' onClick={handleClose} disabled={loading}>
          {t('dashboard.training.modals.addqamodal.cancelButton')}
        </CancelButton>
        <PrimaryButton
          className='primary-btn'
          onClick={handleAddQA}
          disabled={loading || !title || !question || !answer || isContentTooLong}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              {t('dashboard.training.modals.addqamodal.addingButton')}
            </>
          ) : (
            initialData ? t('dashboard.training.modals.addqamodal.saveButton') : t('dashboard.training.modals.addqamodal.addButton')
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddQAModal; 